import { useState } from "react";
import { X, ScanSearch, Crop } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PhotoThumb, Disclaimer } from "@/components/Common";
import PhotoUploader from "@/components/PhotoUploader";
import QuotaBanner from "@/components/QuotaBanner";
import CropDialog from "@/components/analysis/CropDialog";
import { takeUploadTime } from "@/lib/imageTools";

const KINDS = [
  { id: "intera", label: "Pianta intera", hint: "Tutta la pianta, con vaso o base del fusto" },
  { id: "organo", label: "Parte con il problema", hint: "La foglia, il ramo o il frutto colpito" },
  { id: "dettaglio", label: "Primo piano del sintomo", hint: "Da vicino e a fuoco: macchie, insetti, lesioni" },
];
export const KIND_LABEL = {
  intera: "Pianta intera", organo: "Zona interessata", dettaglio: "Dettaglio", pagina_inferiore_foglia: "Pagina inferiore",
  colletto: "Colletto", ramo: "Ramo", corteccia: "Corteccia", frutto: "Frutto", fiore: "Fiore", foglia: "Foglia",
  radici: "Radici", lesione_ravvicinata: "Lesione", foglia_controluce: "Controluce", ritaglio: "Ritaglio", altro: "Altro",
};

export const PhotoGrid = ({ photos, onDelete }) => (
  <div className="grid grid-cols-3 gap-2" data-testid="photo-grid">
    {photos.map((p) => (
      <div key={p.id} className="relative" data-testid={`photo-item-${p.id}`}>
        <PhotoThumb id={p.id} className="aspect-square w-full" />
        <span className="absolute bottom-1 left-1 rounded bg-[#0D2517]/80 px-1.5 py-0.5 text-[11px] text-[#F7F4EE]">{KIND_LABEL[p.kind] || p.kind}</span>
        {onDelete && (
          <button onClick={() => onDelete(p.id)} className="absolute right-1 top-1 flex h-9 w-9 items-center justify-center rounded-full bg-white/90" aria-label="Elimina foto" data-testid={`photo-delete-${p.id}`}>
            <X size={16} />
          </button>
        )}
      </div>
    ))}
  </div>
);

function Slot({ k, n, photo, analysisId, reload, onDelete }) {
  return (
    <div className="rounded-2xl border border-[#ddc69c]/70 bg-white p-4" data-testid={`photo-slot-${k.id}`}>
      <div className="flex items-start gap-3">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-bold ${photo ? "bg-[#15803D] text-white" : "border border-[#C9A96E] text-[#B39257]"}`}>{photo ? "✓" : n}</span>
        <div className="min-w-0 flex-1">
          <p className="text-[18px] font-semibold leading-snug text-[#0D2517]">{k.label}</p>
          <p className="text-[16px] leading-snug text-[#5b6b60]">{k.hint}</p>
        </div>
        {photo && (
          <div className="relative h-16 w-16 shrink-0">
            <PhotoThumb id={photo.id} className="h-16 w-16" />
            <button onClick={() => onDelete(photo.id)} className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full border bg-white" aria-label="Elimina foto" data-testid={`photo-slot-delete-${k.id}`}><X size={14} /></button>
          </div>
        )}
      </div>
      {!photo && <PhotoUploader analysisId={analysisId} kind={k.id} max={1} onUploaded={reload} testid={`uploader-${k.id}`} />}
    </div>
  );
}

export default function PhotoStep({ analysis, reload }) {
  const [cropOpen, setCropOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const photos = analysis.photos || [];
  const crops = photos.filter((p) => !KINDS.some((k) => k.id === p.kind));

  const del = async (id) => {
    await api.delete(`/photos/${id}`).catch((e) => toast.error(errMsg(e)));
    reload();
  };
  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/analyses/${analysis.id}/analyze`, { upload_ms: takeUploadTime(analysis.id) });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 px-5 py-6" data-testid="photo-step">
      <div>
        <h1 className="font-serif text-[26px] font-semibold leading-tight text-[#0D2517]">Fotografa la pianta</h1>
        <p className="mt-2 text-[17px] leading-relaxed text-[#2D3A30]">Bastano tre foto. Ti chiederò altro solo se serve davvero.</p>
      </div>
      <QuotaBanner />
      <div className="space-y-3">
        {KINDS.map((k, i) => (
          <Slot key={k.id} k={k} n={i + 1} photo={photos.find((p) => p.kind === k.id)} analysisId={analysis.id} reload={reload} onDelete={del} />
        ))}
      </div>
      {crops.length > 0 && <PhotoGrid photos={crops} onDelete={del} />}
      <button onClick={() => setCropOpen(true)} className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#C9A96E] px-4 py-2 text-[16px] text-[#0D2517]" data-testid="add-crop-button">
        <Crop size={18} className="shrink-0" /> Insetti o macchie molto piccoli? Aggiungi un ritaglio ravvicinato
      </button>
      <button onClick={start} disabled={!photos.length || busy} className="btn-primary min-h-[52px] w-full !text-base" data-testid="start-analysis-button">
        <ScanSearch size={20} /> ANALIZZA ({photos.length} foto)
      </button>
      <Disclaimer compact />
      <CropDialog analysisId={analysis.id} open={cropOpen} onOpenChange={setCropOpen} onUploaded={reload} />
    </div>
  );
}
